import React, { useState, useEffect } from 'react';
import api from '../services/api';
import { formatCurrency } from '../utils/formatters';
import { CategorySubcategorySelect } from './CategorySubcategorySelect';

interface Transaction {
  id: number;
  date: string;
  amount: number;
  merchant_name: string | null;
  description: string;
  splits?: TransactionSplit[];
}

interface TransactionSplit {
  id: number;
  transaction_id: number;
  category_id: number;
  subcategory_id: number | null;
  amount: number;
  notes: string | null;
  category_name: string;
  category_color?: string;
  subcategory_name: string | null;
}

interface TransactionCategory {
  id: number;
  name: string;
  color: string;
  subcategories: TransactionSubcategory[];
}

interface TransactionSubcategory {
  id: number;
  name: string;
}

interface SplitExpenseModalProps {
  isOpen: boolean;
  onClose: () => void;
  transaction: Transaction | null;
  categories: TransactionCategory[];
  onSplitsUpdated: () => void;
}

interface SplitRow {
  id: string; // temporary ID for new rows
  category_id: number | null;
  subcategory_id: number | null;
  amount: string;
  notes: string;
  isNew?: boolean;
  splitId?: number; // actual split ID from database
}

export const SplitExpenseModal: React.FC<SplitExpenseModalProps> = ({
  isOpen,
  onClose,
  transaction,
  categories,
  onSplitsUpdated,
}) => {
  const [splits, setSplits] = useState<TransactionSplit[]>([]);
  const [splitRows, setSplitRows] = useState<SplitRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen && transaction) {
      loadSplits();
      setError(null);
      setSuccessMessage(null);
    }
  }, [isOpen, transaction]);

  useEffect(() => {
    // Convert splits to rows for editing
    if (splits.length > 0) {
      setSplitRows(splits.map(split => {
        const amount = typeof split.amount === 'number' 
          ? split.amount 
          : parseFloat(String(split.amount || '0'));
        return {
          id: `split-${split.id}`,
          category_id: split.category_id,
          subcategory_id: split.subcategory_id,
          amount: isNaN(amount) ? '' : amount.toFixed(2),
          notes: split.notes || '',
          splitId: split.id,
          isNew: false,
        };
      }));
    } else {
      // Start with two empty rows (splitting requires at least 2 parts)
      setSplitRows([
        {
          id: 'new-1',
          category_id: null,
          subcategory_id: null,
          amount: '',
          notes: '',
          isNew: true,
        },
        {
          id: 'new-2',
          category_id: null,
          subcategory_id: null,
          amount: '',
          notes: '',
          isNew: true,
        }
      ]);
    }
  }, [splits]);

  const loadSplits = async () => {
    if (!transaction) return;
    
    setLoading(true);
    try {
      const splitsData = await api.transactions.getTransactionSplits(transaction.id);
      setSplits(splitsData);
    } catch (err) {
      console.error('Error loading splits:', err);
      setError('Failed to load splits');
    } finally {
      setLoading(false);
    }
  };

  const transactionAmount = transaction ? Math.abs(transaction.amount) : 0;
  const totalSplitAmount = splitRows.reduce((sum, row) => {
    const amount = parseFloat(row.amount) || 0;
    return sum + amount;
  }, 0);
  const remainingAmount = transactionAmount - totalSplitAmount;
  const isFullySplit = Math.abs(remainingAmount) < 0.01;
  
  // Count valid rows (with category and amount)
  const validRowsCount = splitRows.filter(row => 
    row.category_id && row.amount && parseFloat(row.amount) > 0
  ).length;
  
  // Require at least 2 splits to enable save
  const hasMinimumSplits = validRowsCount >= 2;

  const addNewRow = () => {
    setSplitRows([...splitRows, {
      id: `new-${Date.now()}`,
      category_id: null,
      subcategory_id: null,
      amount: '',
      notes: '',
      isNew: true,
    }]);
  };

  const removeRow = (id: string) => {
    setSplitRows(splitRows.filter(row => row.id !== id));
  };

  const updateRow = (id: string, updates: Partial<SplitRow>) => {
    setSplitRows(splitRows.map(row => 
      row.id === id ? { ...row, ...updates } : row
    ));
  };

  const handleSave = async () => {
    if (!transaction) return;

    setError(null);
    setSuccessMessage(null);

    // Validate all rows
    const validRows = splitRows.filter(row => 
      row.category_id && row.amount && parseFloat(row.amount) > 0
    );

    // Require at least 2 splits (splitting should mean dividing into multiple parts)
    if (validRows.length < 2) {
      setError('Please add at least two splits with categories and amounts. If you only need one category, assign it directly to the transaction instead of splitting.');
      return;
    }

    // Check total
    const total = validRows.reduce((sum, row) => sum + parseFloat(row.amount || '0'), 0);
    const tolerance = 0.01;
    if (Math.abs(total - transactionAmount) > tolerance) {
      setError(`Total splits (${formatCurrency(total)}) must exactly equal transaction amount (${formatCurrency(transactionAmount)}). Difference: ${formatCurrency(Math.abs(total - transactionAmount))}`);
      return;
    }

    setLoading(true);
    try {
      // Delete existing splits that are no longer in rows
      const existingSplitIds = splits.map(s => s.id);
      const currentSplitIds = splitRows.filter(r => r.splitId).map(r => r.splitId!);
      const toDelete = existingSplitIds.filter(id => !currentSplitIds.includes(id));
      
      for (const splitId of toDelete) {
        await api.transactions.deleteTransactionSplit(splitId);
      }

      // Reload splits after deletions to check if automatic conversion happened
      // (If only one split remained, backend converts it to direct category and deletes it)
      const splitsAfterDeletion = await api.transactions.getTransactionSplits(transaction.id);
      setSplits(splitsAfterDeletion);

      // If splits were automatically converted (no splits remain), skip update/create
      // The transaction is now categorized directly, so we're done
      if (splitsAfterDeletion.length === 0 && toDelete.length > 0 && currentSplitIds.length === 1) {
        // Automatic conversion happened - transaction is now directly categorized
        setSuccessMessage('Split converted to regular category successfully!');
        onSplitsUpdated();
        setTimeout(() => {
          setSuccessMessage(null);
          onClose();
        }, 1000);
        return;
      }

      // Update or create splits (only if we still have splits to manage)
      for (const row of validRows) {
        if (row.splitId) {
          // Update existing split (only if it still exists - conversion might have deleted it)
          try {
            await api.transactions.updateTransactionSplit(row.splitId, {
              category_id: row.category_id!,
              subcategory_id: row.subcategory_id,
              amount: parseFloat(row.amount),
              notes: row.notes || undefined,
            });
          } catch (err: any) {
            // Split might have been deleted by automatic conversion - skip if 404
            if (err.message && err.message.includes('404')) {
              continue;
            }
            throw err;
          }
        } else {
          // Create new split
          await api.transactions.createTransactionSplit(transaction.id, {
            category_id: row.category_id!,
            subcategory_id: row.subcategory_id,
            amount: parseFloat(row.amount),
            notes: row.notes || undefined,
          });
        }
      }

      await loadSplits();
      setSuccessMessage('Splits saved successfully!');
      onSplitsUpdated();
      // Close modal after a brief delay to show success message
      setTimeout(() => {
        setSuccessMessage(null);
        onClose();
      }, 1000);
    } catch (err: any) {
      console.error('Error saving splits:', err);
      // Extract the actual error message
      let errorMessage = 'Failed to save splits';
      if (err.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !transaction) return null;

  const isIncome = transaction.amount > 0;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="modal-content split-expense-modal" 
        onClick={(e) => e.stopPropagation()} 
        style={{ 
          maxWidth: '800px', 
          width: '90%',
          background: 'var(--background-card)',
          color: 'var(--text-primary)'
        }}
      >
        <div className="modal-header" style={{ 
          padding: '20px', 
          borderBottom: '1px solid var(--border-color)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <h2 style={{ margin: 0, color: 'var(--text-primary)' }}>Split Transaction</h2>
          <button 
            className="modal-close" 
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: 'var(--text-secondary)',
              padding: '0',
              width: '30px',
              height: '30px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            ×
          </button>
        </div>
        
        <div className="modal-body" style={{ padding: '20px' }}>
          {/* Transaction Summary */}
          <div style={{ 
            background: 'var(--background-secondary)', 
            padding: '15px', 
            borderRadius: '8px', 
            marginBottom: '20px',
            border: '1px solid var(--border-color)'
          }}>
            <div style={{ marginBottom: '10px', fontWeight: '600', color: 'var(--text-primary)' }}>
              {transaction.merchant_name || transaction.description}
            </div>
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', fontSize: '14px' }}>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Date: </span>
                <span style={{ color: 'var(--text-primary)' }}>
                  {new Date(transaction.date).toLocaleDateString()}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Total: </span>
                <span style={{ fontWeight: 'bold', color: isIncome ? '#10b981' : '#ef4444' }}>
                  {formatCurrency(transactionAmount)}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Split Total: </span>
                <span style={{ fontWeight: 'bold', color: 'var(--text-primary)' }}>
                  {formatCurrency(totalSplitAmount)}
                </span>
              </div>
              <div>
                <span style={{ color: 'var(--text-secondary)' }}>Remaining: </span>
                <span style={{ 
                  fontWeight: 'bold',
                  color: isFullySplit ? '#10b981' : remainingAmount > 0 ? '#f59e0b' : '#ef4444'
                }}>
                  {formatCurrency(remainingAmount)}
                </span>
              </div>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div style={{ 
              padding: '12px', 
              background: 'rgba(239, 68, 68, 0.1)', 
              border: '1px solid rgba(239, 68, 68, 0.3)', 
              borderRadius: '6px', 
              color: '#ef4444',
              marginBottom: '15px'
            }}>
              <strong>Error:</strong> {error}
            </div>
          )}

          {successMessage && (
            <div style={{ 
              padding: '12px', 
              background: 'rgba(16, 185, 129, 0.1)', 
              border: '1px solid rgba(16, 185, 129, 0.3)', 
              borderRadius: '6px', 
              color: '#10b981',
              marginBottom: '15px'
            }}>
              {successMessage}
            </div>
          )}

          {/* Splits Table */}
          <div style={{ marginBottom: '20px' }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: '15px'
            }}>
              <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '16px' }}>
                Splits ({splitRows.length})
              </h3>
              <button
                onClick={addNewRow}
                style={{
                  padding: '8px 16px',
                  background: 'var(--primary-color)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
              >
                + Add Row
              </button>
            </div>

            <div style={{
              border: '1px solid var(--border-color)',
              borderRadius: '8px',
              overflow: 'hidden',
              background: 'var(--background-card)'
            }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ 
                    background: 'var(--background-secondary)',
                    borderBottom: '2px solid var(--border-color)'
                  }}>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      fontSize: '13px'
                    }}>
                      Category
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      width: '150px'
                    }}>
                      Amount
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'left', 
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      fontSize: '13px'
                    }}>
                      Notes
                    </th>
                    <th style={{ 
                      padding: '12px', 
                      textAlign: 'center', 
                      fontWeight: '600',
                      color: 'var(--text-primary)',
                      fontSize: '13px',
                      width: '80px'
                    }}>
                      Action
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {splitRows.map((row, index) => (
                    <tr 
                      key={row.id}
                      style={{ 
                        borderBottom: index < splitRows.length - 1 ? '1px solid var(--border-color)' : 'none'
                      }}
                    >
                      <td style={{ padding: '12px' }}>
                        <CategorySubcategorySelect
                          categories={categories}
                          currentCategoryId={row.category_id}
                          currentSubcategoryId={row.subcategory_id}
                          isIncome={isIncome}
                          onChange={(catId, subcatId) => {
                            updateRow(row.id, { 
                              category_id: catId, 
                              subcategory_id: subcatId 
                            });
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="number"
                          step="0.01"
                          min="0.01"
                          value={row.amount}
                          onChange={(e) => updateRow(row.id, { amount: e.target.value })}
                          placeholder="0.00"
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: 'var(--text-primary)',
                            background: 'var(--input-background)',
                            textAlign: 'right'
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px' }}>
                        <input
                          type="text"
                          value={row.notes}
                          onChange={(e) => updateRow(row.id, { notes: e.target.value })}
                          placeholder="Optional notes"
                          style={{ 
                            width: '100%', 
                            padding: '8px', 
                            border: '1px solid var(--input-border)',
                            borderRadius: '4px',
                            fontSize: '14px',
                            color: 'var(--text-primary)',
                            background: 'var(--input-background)'
                          }}
                        />
                      </td>
                      <td style={{ padding: '12px', textAlign: 'center' }}>
                        {splitRows.length > 1 && (
                          <button
                            onClick={() => removeRow(row.id)}
                            style={{
                              padding: '6px 12px',
                              background: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px'
                            }}
                          >
                            Remove
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ 
                    background: 'var(--background-secondary)',
                    borderTop: '2px solid var(--border-color)'
                  }}>
                    <td style={{ padding: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>
                      Total
                    </td>
                    <td style={{ 
                      padding: '12px', 
                      textAlign: 'right', 
                      fontWeight: 'bold',
                      color: isFullySplit ? '#10b981' : remainingAmount > 0 ? '#f59e0b' : '#ef4444',
                      fontSize: '16px'
                    }}>
                      {formatCurrency(totalSplitAmount)}
                    </td>
                    <td colSpan={2} style={{ padding: '12px' }}>
                      {isFullySplit ? (
                        <span style={{ color: '#10b981', fontSize: '13px' }}>
                          ✓ Fully split
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                          {formatCurrency(remainingAmount)} remaining
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            <div style={{ 
              marginTop: '12px', 
              padding: '10px', 
              background: 'var(--background-secondary)',
              borderRadius: '6px',
              fontSize: '13px',
              color: 'var(--text-secondary)'
            }}>
              <strong style={{ color: 'var(--text-primary)' }}>Note:</strong> All splits must exactly equal the transaction total of {formatCurrency(transactionAmount)}.
            </div>
          </div>
        </div>

        <div className="modal-footer" style={{ 
          padding: '15px 20px', 
          borderTop: '1px solid var(--border-color)', 
          display: 'flex', 
          justifyContent: 'flex-end',
          gap: '10px'
        }}>
          <button 
            onClick={onClose} 
            disabled={loading}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              background: 'var(--background-secondary)',
              color: 'var(--text-primary)',
              border: '1px solid var(--border-color)',
              borderRadius: '6px',
              cursor: loading ? 'not-allowed' : 'pointer',
              fontWeight: '500'
            }}
          >
            Cancel
          </button>
          <button 
            onClick={handleSave}
            disabled={loading || !isFullySplit || !hasMinimumSplits}
            style={{
              padding: '10px 20px',
              fontSize: '14px',
              background: (!isFullySplit || !hasMinimumSplits || loading) ? 'var(--background-tertiary)' : 'var(--primary-color)',
              color: (!isFullySplit || !hasMinimumSplits || loading) ? 'var(--text-secondary)' : 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: (!isFullySplit || !hasMinimumSplits || loading) ? 'not-allowed' : 'pointer',
              fontWeight: '600'
            }}
          >
            {loading ? 'Saving...' : 'Save Splits'}
          </button>
        </div>
      </div>
    </div>
  );
};
